import { useEffect, useState, useCallback, useMemo, useRef, forwardRef, useImperativeHandle, Fragment } from "react"
import { useDispatch, useSelector } from "react-redux"
import { fetchRelatedItems } from "../api/tableData"
import { fetchComponentsFromAPI } from "../api/componentsApi"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Checkbox } from "@/components/ui/checkbox"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Alert, AlertDescription } from "@/components/ui/alert"
import RelationField from "../RelationField"
import RichTextEdit from "../editors/RichTextEdit"
import EditCalendar from "../editors/EditCalendar"
import { DateTime } from "../DateTime"
import { DateOnly } from "../DateOnly"
import { TimeOnly } from "../TimeOnly"
import { ActionTypes } from "@/actions"
// Removed direct database imports - using API instead
import { itemUpdate } from "../api/itemData"
import { dataAPI } from "../api/dataApi"
import Image from "../Image"
import FileField from "../FileField"
import { finalizeFile, addFileLink } from "../api/file"
import { Save, Plus, AlertCircle, Calendar, Image as ImageIcon, Type, Link, Code, CheckSquare, Hash, List, File, MapPin } from "lucide-react"
import { useAuth } from "../auth/AuthContext"
import { resolveCollectionIdentifier, getMongoCollectionNameForFolder } from "../utils/collectionUtils"
import { buildCollectionTableKey, normalizeOrgSegment } from "../utils/collectionPaths"
import DebugFieldButton from "@/components/DebugFieldButton"
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select"
import { EMPTY_ARRAY, EMPTY_OBJECT } from "@/constants/reduxStableRefs"
import { effectiveImageFieldType, resolveConnectorAssetPublicUrl } from "../utils/pictureUtils"
import { GpxFieldControl } from "./GpxFieldControl"
import {
    appendGpxTrackPointAPI,
    gpxSetRecordingAPI,
} from "../api/itemsApi"

const EMPTY_SELECT_VALUE = "__none__"

function ItemSelectField({
    field,
    options,
    rawVal,
}: {
    field: any
    options: string[]
    rawVal: unknown
}) {
    const dispatch = useDispatch()
    const selectValue =
        rawVal === null || rawVal === undefined ? "" : String(rawVal)
    const allowEmpty = field.allowEmptyOption !== false
    const firstOptionFull = useMemo(
        () => (options.length ? options[0] : undefined),
        [options]
    )

    useEffect(() => {
        if (allowEmpty) return
        if (firstOptionFull === undefined) return
        if (selectValue !== "") return
        dispatch({
            type: ActionTypes.EDIT_ITEM_UPDATED,
            payload: { key: field.name, value: firstOptionFull },
        })
    }, [allowEmpty, firstOptionFull, selectValue, field.name, dispatch])

    const controlled =
        !allowEmpty && selectValue === "" && firstOptionFull !== undefined
            ? firstOptionFull
            : selectValue
    const radixValue = controlled === "" ? EMPTY_SELECT_VALUE : controlled

    return (
        <Select
            value={radixValue}
            onValueChange={(value) => {
                const v = value === EMPTY_SELECT_VALUE ? "" : value
                dispatch({
                    type: ActionTypes.EDIT_ITEM_UPDATED,
                    payload: { key: field.name, value: v },
                })
            }}
        >
            <SelectTrigger className="w-full">
                <SelectValue placeholder="Select" />
            </SelectTrigger>
            <SelectContent>
                {allowEmpty && (
                    <SelectItem value={EMPTY_SELECT_VALUE}>Select</SelectItem>
                )}
                {options.map((option) => (
                    <SelectItem key={option} value={option}>
                        {option}
                    </SelectItem>
                ))}
            </SelectContent>
        </Select>
    )
}

const ITEM_COMPARE_IGNORE_KEYS = new Set(["_updated"])

function normalizeItemForCompare(item: any, pendingFiles?: Record<string, string>) {
    if (!item || typeof item !== "object") {
        return item
    }
    const merged = pendingFiles ? { ...item, ...pendingFiles } : item
    const normalized: Record<string, unknown> = {}
    for (const [key, value] of Object.entries(merged)) {
        if (ITEM_COMPARE_IGNORE_KEYS.has(key)) {
            continue
        }
        normalized[key] = value
    }
    return normalized
}

const EditItem = forwardRef<{ save: () => Promise<void>; isLoading: boolean; valid: boolean; isNew: boolean; hasUnsavedChanges: () => boolean }, {
    onEdited: any,
    hideFooterButton?: boolean,
    onCancel?: () => void,
}>(({
    onEdited: onEdited,
    hideFooterButton = false,
    onCancel,
}, ref) => {

    const dispatch = useDispatch()
    const { user: authUser } = useAuth()
    const _meta = useSelector((state: any) => state.table.meta ?? EMPTY_ARRAY)
    const _currentItem = useSelector((state: any) => state.item.current ?? EMPTY_OBJECT)
    const _currentItemUid = useSelector((state: any) => state.item.current?.uid)
    const isNew = useSelector((state: any) => state.item.isNew)
    const slug = useSelector((state: any) => state.item.currentSlug)
    const collectionFromRedux = useSelector((state: any) => state.item.currentCollection)
    const tableListCtxKey = useSelector((state: any) => state.table.currentSlug)

    // Use ref to store the previous item and only update when uid changes
    const currentItemRef = useRef(_currentItem)
    const previousUidRef = useRef(_currentItemUid)
    const lastSavedItemRef = useRef<any>(null)
    const latestItemRef = useRef(_currentItem)
    const snapshotIdentityRef = useRef<string | null>(null)

    // Only update the ref when the uid actually changes
    if (_currentItemUid !== previousUidRef.current) {
        currentItemRef.current = _currentItem
        previousUidRef.current = _currentItemUid
    }

    const prevUidRef = useRef<string | undefined>(undefined)
    useEffect(() => {
        if (prevUidRef.current !== undefined && prevUidRef.current !== _currentItemUid) {
            setPendingFileValues({})
        }
        prevUidRef.current = _currentItemUid
    }, [_currentItemUid])

    useEffect(() => {
        latestItemRef.current = _currentItem;
    }, [_currentItem]);

    const cloneItem = useCallback((item: any) => {
        if (!item) {
            return {};
        }
        try {
            return JSON.parse(JSON.stringify(item));
        } catch (error) {
            console.warn('[EditItem] Failed to clone item snapshot', error);
            return { ...item };
        }
    }, []);

    const currentObjectId = _currentItem?.objectId;
    const currentMongoId = _currentItem?._id;

    const savedStateIdentity = useMemo(() => {
        return [
            isNew ? 'new' : 'existing',
            slug || 'noslug',
            _currentItemUid || 'nouid',
            currentMongoId || 'no_mongo_id',
            currentObjectId || 'no_object_id',
        ].join(':');
    }, [isNew, slug, _currentItemUid, currentMongoId, currentObjectId]);

    useEffect(() => {
        if (snapshotIdentityRef.current === savedStateIdentity) {
            return;
        }
        snapshotIdentityRef.current = savedStateIdentity;
        lastSavedItemRef.current = cloneItem(latestItemRef.current);
        setErrorMessage(null);
    }, [cloneItem, savedStateIdentity]);

    // Memoize currentItem to only change when uid changes, not on every Redux update
    const currentItem = useMemo(() => currentItemRef.current, [_currentItemUid])

    const [meta, setMeta] = useState<any>(undefined)
    const [relatedItems, setRelatedItems] = useState<any>()
    const _user = useSelector((state: any) => state.user)
    // Use auth context user if available, fallback to Redux user (same as ComponentTable)
    const user = authUser || _user
    const [valid, setValid] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const [errorMessage, setErrorMessage] = useState<string | null>(null)
    const [followUpNotice, setFollowUpNotice] = useState<string | null>(null)
    const [formResetToken, setFormResetToken] = useState(0)
    const [pendingFileValues, setPendingFileValues] = useState<Record<string, string>>({})
    const [generatingCodeField, setGeneratingCodeField] = useState<string | null>(null)

    useEffect(() => {
        setValid(!!user);
    }, [user]);

    const resetFormToLastSaved = useCallback(() => {
        if (!lastSavedItemRef.current) {
            console.warn('[EditItem] No snapshot available to reset form');
            return;
        }
        const snapshot = cloneItem(lastSavedItemRef.current);
        dispatch({ type: ActionTypes.EDIT_ITEM_RESET_CURRENT, payload: snapshot });
        setFormResetToken((token) => token + 1);
    }, [cloneItem, dispatch]);

    const buildFollowUpNotice = (notifications: any) => {
        if (!notifications || typeof notifications !== 'object') {
            return null;
        }
        const emailTriggered = Number(notifications.emailTriggered || 0);
        const emailSent = Number(notifications.emailSent || 0);
        const emailFailed = Number(notifications.emailFailed || 0);

        if (!emailTriggered) {
            return null;
        }

        const suffix = emailTriggered === 1 ? 'submission' : 'submissions';

        if (emailFailed > 0 && emailSent > 0) {
            return `Follow-up email triggered for ${emailTriggered} ${suffix}. ${emailSent} sent, ${emailFailed} failed.`;
        }

        if (emailFailed > 0) {
            return `Follow-up email triggered for ${emailTriggered} ${suffix}, but it failed to send. Check the email service.`;
        }

        if (emailSent > 0) {
            return `Follow-up email sent to the submitter.`;
        }

        return `Follow-up email triggered for ${emailTriggered} ${suffix}.`;
    };

    const handleUpdateItem = async () => {
        // IMPORTANT: Use _currentItem (latest Redux state) for saving, not memoized currentItem
        const itemToSave = _currentItem;

        let collection = resolveCollectionIdentifier(meta, slug);

        if (!collection && slug && Array.isArray(_meta)) {
            const metaFromArray =
                (tableListCtxKey ? _meta.find((m: any) => m && m.tableKey === tableListCtxKey) : undefined) ||
                _meta.find((m: any) => m && m.slug === slug);
            collection = resolveCollectionIdentifier(metaFromArray, slug);
        }

        if (!collection && slug) {
            try {
                const components = await fetchComponentsFromAPI();
                const component = components.find((comp: any) =>
                    comp.slug === slug ||
                    comp.collection === slug ||
                    (comp as any).mongoCollection === slug
                );

                if (component) {
                    collection = resolveCollectionIdentifier(component, slug);
                }
            } catch (error) {
                console.error('Failed to fetch component from API:', error);
            }
        }

        if (!collection && collectionFromRedux) {
            collection = collectionFromRedux;
        }

        if (!collection && slug) {
            collection = slug;
        }

        const resolvedCollection = collection;

        if (!resolvedCollection) {
            console.error('❌ EditItem: Cannot update item - collection is undefined', {
                hasMeta: !!meta,
                dataSource: meta?.dataSource,
                collectionFromRedux,
                slug,
                _metaLength: _meta?.length,
            });
            alert('Error: Unable to update item. Collection information is missing. Check console for details.');
            return;
        }

        console.log('[EditItem] Collection resolution', {
            slug,
            collection: resolvedCollection,
            itemId: itemToSave?.uid || 'new',
            usedReduxFallback: resolvedCollection === collectionFromRedux,
            usedSlugFallback: resolvedCollection === slug
        });

        // Log item structure to debug missing objectId/_id
        console.log('[EditItem] Item structure check:', {
            hasObjectId: !!itemToSave?.objectId,
            has_id: !!itemToSave?._id,
            hasUid: !!itemToSave?.uid,
            objectId: itemToSave?.objectId,
            _id: itemToSave?._id,
            uid: itemToSave?.uid,
            allKeys: Object.keys(itemToSave || {})
        });

        // Use slug for API calls - backend resolves slug to appropriate collection
        const slugToUse = slug || resolvedCollection;
        if (!slugToUse) {
            console.error('❌ EditItem: Cannot update item - slug is undefined');
            alert('Error: Unable to update item. Slug information is missing.');
            return;
        }

        setErrorMessage(null);
        setFollowUpNotice(null);
        setIsLoading(true)
        try {
            // Get field definitions from meta to properly convert date fields
            const fields = meta?.fields || [];

            if (isNew) {
                const response = await itemUpdate(itemToSave, slugToUse, fields, user, { isNew: true });
                const savedItem = response?.item ?? response?.data ?? response;
                const itemUid = savedItem?.uid || itemToSave.uid;
                const userUid = user?.uid || user?.info?.uid;
                const finalizeCollection = getMongoCollectionNameForFolder(meta, slugToUse) || resolvedCollection || slugToUse;

                const fileUpdates: Record<string, string> = {};
                const fileFields = (meta?.fields || []).filter((f: any) => f.type === 'file');
                for (const field of fileFields) {
                    const val = itemToSave[field.name];
                    if (typeof val === 'string' && val.startsWith('temp/')) {
                        const finalizeRes = await finalizeFile({
                            filename: val,
                            collection: finalizeCollection,
                            uid: itemUid,
                            field: field.name,
                            userId: userUid
                        });
                        if (finalizeRes?.success && finalizeRes.filename) {
                            fileUpdates[field.name] = finalizeRes.filename;
                        } else {
                            throw new Error(finalizeRes?.message || 'Failed to finalize file. The temporary file may have been removed.');
                        }
                    } else if (typeof val === 'string' && val.trim()) {
                        await addFileLink({
                            collection: finalizeCollection,
                            itemUid: String(itemUid),
                            field: field.name,
                            filename: val.trim(),
                        });
                    }
                }
                if (Object.keys(fileUpdates).length > 0) {
                    const patch = { ...savedItem, ...fileUpdates };
                    delete (patch as any)._id;
                    await itemUpdate(patch, slugToUse, fields, user);
                }

                const updatedComponent = { ...itemToSave, ...savedItem, ...fileUpdates, uid: itemUid };
                onEdited(updatedComponent);

                const orgNorm = meta?.organization ? normalizeOrgSegment(meta.organization) : undefined;
                const tableKey =
                    meta?.tableKey ||
                    (slugToUse ? buildCollectionTableKey(slugToUse, meta?.organization) : undefined);
                dispatch({
                    type: ActionTypes.TABLE_REQUEST,
                    payload: {
                        slugName: slugToUse,
                        user,
                        tableKey,
                        organization: orgNorm || undefined,
                        component: meta,
                    }
                });
            } else {
                // Update existing item via API
                const response = await itemUpdate(itemToSave, slugToUse, fields, user);
                const followUpMessage = buildFollowUpNotice(response?.followUpNotifications);
                setFollowUpNotice(followUpMessage);
                onEdited();

                const orgNorm = meta?.organization ? normalizeOrgSegment(meta.organization) : undefined;
                const tableKey =
                    meta?.tableKey ||
                    (slugToUse ? buildCollectionTableKey(slugToUse, meta?.organization) : undefined);
                dispatch({
                    type: ActionTypes.TABLE_REQUEST,
                    payload: {
                        slugName: slugToUse,
                        user,
                        tableKey,
                        organization: orgNorm || undefined,
                        component: meta,
                    }
                });
            }
            lastSavedItemRef.current = cloneItem(latestItemRef.current);
        } catch (error: any) {
            console.error('Error updating item:', error);
            const message = error?.message || error?.response?.data?.message || error?.data?.message;
            setErrorMessage(message ? `Unable to save: ${message}` : 'Unable to save changes. Previous values have been restored.');
            resetFormToLastSaved();
        } finally {
            setIsLoading(false)
        }
    };

    const hasUnsavedChanges = useCallback(() => {
        if (!lastSavedItemRef.current) {
            return false
        }
        try {
            const current = normalizeItemForCompare(latestItemRef.current, pendingFileValues)
            const saved = normalizeItemForCompare(lastSavedItemRef.current)
            return JSON.stringify(current) !== JSON.stringify(saved)
        } catch {
            return true
        }
    }, [pendingFileValues])

    useImperativeHandle(ref, () => ({
        save: handleUpdateItem,
        isLoading,
        valid,
        isNew,
        hasUnsavedChanges,
    }));

    const handleInputChange = useCallback((e: any) => {
        dispatch({ type: ActionTypes.EDIT_ITEM_UPDATED, payload: { key: e.target.name, value: e.target.value || '' } })
    }, [dispatch])

    const capitalizeStart = (token: string) => {
        if (!token) return token;
        return `${token.charAt(0).toUpperCase()}${token.slice(1)}`;
    };

    const toTitleCase = (value: string) => {
        return value
            .split(/(\s+)/)
            .map((part) => {
                if (!part || /^\s+$/.test(part)) {
                    return part;
                }
                return part
                    .split(/([\-'])/)
                    .map((segment) => (segment === "-" || segment === "'" ? segment : `${segment.charAt(0).toUpperCase()}${segment.slice(1).toLowerCase()}`))
                    .join("");
            })
            .join("");
    };

    const toNameCase = (value: string) => {
        const parts = value.split(/(\s+)/);
        const wordIndexes = parts
            .map((part, index) => ({ part, index }))
            .filter(({ part }) => part && !/^\s+$/.test(part))
            .map(({ index }) => index);

        if (wordIndexes.length === 0) {
            return value;
        }

        const firstWordIndex = wordIndexes[0];
        const lastWordIndex = wordIndexes[wordIndexes.length - 1];

        return parts
            .map((part, index) => {
                if (!part || /^\s+$/.test(part)) {
                    return part;
                }
                if (index !== firstWordIndex && index !== lastWordIndex) {
                    return part;
                }
                return part
                    .split(/([\-'])/)
                    .map((segment) => (segment === "-" || segment === "'" ? segment : capitalizeStart(segment)))
                    .join("");
            })
            .join("");
    };

    const applyTextCaseRule = (value: string, caseRule?: string) => {
        switch (caseRule) {
            case "lower":
                return value.toLowerCase();
            case "title":
                return toTitleCase(value);
            case "name":
                return toNameCase(value);
            default:
                return value;
        }
    };

    const handleTextInputChange = useCallback((event: any, field: any) => {
        const rawValue = String(event.target.value ?? "");
        const normalizedValue = applyTextCaseRule(rawValue, field?.case);
        // Keep uncontrolled input UI in sync immediately while typing.
        event.target.value = normalizedValue;
        dispatch({ type: ActionTypes.EDIT_ITEM_UPDATED, payload: { key: event.target.name, value: normalizedValue } });
    }, [dispatch])

    const handleNumberChange = useCallback((e: any) => {
        const value = e.target.value === '' ? null : (e.target.value === '-' ? null : Number(e.target.value));
        dispatch({ type: ActionTypes.EDIT_ITEM_UPDATED, payload: { key: e.target.name, value: isNaN(value as number) ? null : value } })
    }, [dispatch])

    const handleDropdownChange = useCallback((e: any) => {
        // Preserve empty string values, only convert null/undefined to empty string
        const value = e.value ?? '';
        dispatch({ type: ActionTypes.EDIT_ITEM_UPDATED, payload: { key: e.key, value } })
    }, [dispatch])

    const handleImageChange = useCallback((key: any, img: any) => {
        dispatch({ type: ActionTypes.EDIT_ITEM_UPDATED, payload: { key: key, value: img.filename || '' } })
    }, [dispatch])

    const handleFileChange = useCallback((key: string, data: { filename: string }) => {
        const value = data.filename || ''
        setPendingFileValues((prev) => ({ ...prev, [key]: value }))
        dispatch({ type: ActionTypes.EDIT_ITEM_UPDATED, payload: { key, value } })
    }, [dispatch])


    const handleCalendarChange = useCallback((data: any) => {
        // Just dispatch the update directly - no need to iterate through keys
        dispatch({ type: ActionTypes.EDIT_ITEM_UPDATED, payload: { key: data.name, value: data.value } })
    }, [dispatch])

    const handleDateTimeChange = useCallback((data: any) => {
        if (!data.datetime) {
            dispatch({ type: ActionTypes.EDIT_ITEM_UPDATED, payload: { key: data.name, value: null } });
            return;
        }

        // Handle Date object or timestamp
        let timestamp: number;
        if (data.datetime instanceof Date) {
            timestamp = data.datetime.getTime();
        } else if (typeof data.datetime === 'number') {
            // If it's already a timestamp, check if it's in seconds or milliseconds
            // Timestamps > 1e12 are in milliseconds, <= 1e12 are in seconds
            timestamp = data.datetime > 1e12 ? data.datetime : data.datetime * 1000;
        } else {
            // Try to parse as date string
            timestamp = new Date(data.datetime).getTime();
        }

        // Convert to seconds (Unix timestamp)
        const value = Math.round(timestamp / 1000);
        console.log('[EditItem] handleDateTimeChange:', {
            original: data.datetime,
            timestamp,
            value,
            date: new Date(timestamp).toISOString()
        });
        dispatch({ type: ActionTypes.EDIT_ITEM_UPDATED, payload: { key: data.name, value } });
    }, [dispatch])

    const handleCheckboxChange = useCallback((checked: boolean, fieldName: string) => {
        dispatch({ type: ActionTypes.EDIT_ITEM_UPDATED, payload: { key: fieldName, value: checked } })
    }, [dispatch])

    const handleHtmlChange = useCallback((data: any) => {
        const key = typeof data?.name === 'string' && data.name.trim()
            ? data.name.trim()
            : (typeof data?.key === 'string' ? data.key.trim() : '');
        if (!key) {
            console.warn('[EditItem] RichText update ignored (missing field name)', data);
            return;
        }
        dispatch({ type: ActionTypes.EDIT_ITEM_UPDATED, payload: { key, value: data.value } })
    }, [dispatch])

    const getFieldIcon = (fieldType: string) => {
        switch (fieldType) {
            case "text": return <Type className="w-4 h-4 text-blue-500" />
            case "textarea": return <Type className="w-4 h-4 text-blue-500" />
            case "instruction": return <Type className="w-4 h-4 text-slate-500" />
            case "image": return <ImageIcon className="w-4 h-4 text-green-500" />
            case "file": return <File className="w-4 h-4 text-amber-500" />
            case "relation": return <Link className="w-4 h-4 text-purple-500" />
            case "multiplerelation":
            case "multiplerelations":
            case "systemrelation":
                return <Link className="w-4 h-4 text-purple-500" />
            case "checkbox": return <CheckSquare className="w-4 h-4 text-orange-500" />
            case "richtext": return <Type className="w-4 h-4 text-emerald-500" />
            case "calendar": return <Calendar className="w-4 h-4 text-indigo-500" />
            case "datetime": return <Calendar className="w-4 h-4 text-indigo-500" />
            case "date":
            case "dateonly": return <Calendar className="w-4 h-4 text-indigo-500" />
            case "timeonly": return <Calendar className="w-4 h-4 text-indigo-500" />
            case "number": return <Hash className="w-4 h-4 text-cyan-500" />
            case "select":
            case "dropdown":
                return <List className="w-4 h-4 text-slate-500" />
            case "code": return <Code className="w-4 h-4 text-amber-500" />
            case "gpx": return <MapPin className="w-4 h-4 text-rose-500" />
            default: return <Type className="w-4 h-4 text-gray-500" />
        }
    }

    const getNestedValue = (obj: any, path?: string) => {
        if (!obj || typeof obj !== "object" || !path) return undefined;
        if (!path.includes(".")) return obj[path];
        return path.split(".").reduce((acc: any, key: string) => (acc && typeof acc === "object" ? acc[key] : undefined), obj);
    };

    const getSystemRelationValue = (item: any, field: any) => {
        if (!item || !field) return undefined;
        if (field.sourceField) {
            return getNestedValue(item, field.sourceField);
        }
        if (field.value === 'forms' && item.registrationFormUid) {
            return item.registrationFormUid;
        }
        return getNestedValue(item, field.name);
    };

    const parseSelectOptions = (value: any): string[] => {
        if (!value) return [];
        if (Array.isArray(value)) {
            return value.map((option) => String(option).trim()).filter(Boolean);
        }
        if (typeof value === "string") {
            return value.split(",").map((option) => option.trim()).filter(Boolean);
        }
        return [];
    };

    // Ownership UI removed (deprecated)

    const renderField = (field: any, currentItem: any) => {
        if (currentItem === null) {
            return (
                <div className="flex items-center justify-center py-4">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                    <span className="ml-2 text-sm text-gray-500">Loading...</span>
                </div>
            );
        }

        const componentFields = Array.isArray(meta?.fields) ? meta.fields : [];
        const currentFieldIndex = componentFields.findIndex((candidate: any) => {
            if (candidate === field) return true;
            if (candidate?.uid && field?.uid) return candidate.uid === field.uid;
            if (candidate?.name && field?.name) return candidate.name === field.name;
            return false;
        });
        const previousField = currentFieldIndex > 0 ? componentFields[currentFieldIndex - 1] : null;
        const nextField = currentFieldIndex >= 0 ? componentFields[currentFieldIndex + 1] : null;
        const leadingInstruction = previousField?.type === 'instruction' ? previousField : null;

        const renderInstructionContent = (instructionField: any) => {
            const html = typeof instructionField?.value === "string" ? instructionField.value : "";
            if (!html.trim()) return null;
            return (
                <div
                    className="text-sm text-gray-700 [&_p]:mb-2 [&_p:last-child]:mb-0"
                    dangerouslySetInnerHTML={{ __html: html }}
                />
            );
        };

        const fieldValueForType = _currentItem?.[field.name] ?? currentItem?.[field.name]
        const effectiveFieldType = effectiveImageFieldType(field, fieldValueForType)
        const connectorAssetPublicUrl =
            field.connectorAssetPublicUrl ||
            meta?.connectorAssetPublicUrl ||
            resolveConnectorAssetPublicUrl({ ...meta, slug: meta?.slug || slug })
        const pictureFilename =
            _currentItem?.[field.name] ?? currentItem?.[field.name] ?? ""

        const fieldCard = (content: React.ReactNode, options?: { hideHeader?: boolean }) => {
            const hideHeader = options?.hideHeader === true
            // Get related data for relation fields
            // Use _currentItem (latest Redux state) instead of memoized currentItem
            let relatedData = undefined
            const isRelationFieldType = field.type === 'relation' || field.type === 'multiplerelation' || field.type === 'systemrelation';
            const relationValueKey = field.type === 'systemrelation' ? field.sourceField || field.name : field.name;
            if (isRelationFieldType) {
                const fieldValue = field.type === 'systemrelation'
                    ? getSystemRelationValue(_currentItem, field)
                    : getNestedValue(_currentItem, relationValueKey)

                // Find the related items for this field from the fetched relatedItems
                // relatedItems is an array of { field: string, items: any[] }
                // field.value is the table/slug (e.g., "speakers")
                if (relatedItems && Array.isArray(relatedItems)) {
                    // Try to match by field.value (table/slug)
                    // Also try field.table if it exists, and handle potential slug variations
                    const relatedItemsForField = relatedItems.find((rel: any) => {
                        const relField = typeof rel.field === 'string' ? rel.field.replace(/[!]+$/, '').trim() : rel.field
                        const fieldValue = typeof field.value === 'string' ? field.value.replace(/[!]+$/, '').trim() : field.value
                        const fieldTable = field.table ? (typeof field.table === 'string' ? field.table.replace(/[!]+$/, '').trim() : field.table) : null

                        return relField === fieldValue ||
                            relField === fieldTable ||
                            relField === field.value ||
                            rel.field === fieldValue
                    })

                    if (relatedItemsForField && relatedItemsForField.items && Array.isArray(relatedItemsForField.items)) {
                        if (field.type === 'relation' || field.type === 'systemrelation') {
                            // Single relation - find the matching item if value is set
                            if (fieldValue !== null && fieldValue !== undefined) {
                                const relatedUid = fieldValue
                                relatedData = relatedItemsForField.items.find((item: any) => item.uid === relatedUid)
                            } else {
                                // No value set, but include all available items for reference
                                relatedData = { availableItems: relatedItemsForField.items }
                            }
                        } else if (field.type === 'multiplerelation') {
                            // Multiple relation - find all matching items
                            const relatedUids = Array.isArray(fieldValue) ? fieldValue : (fieldValue ? [fieldValue] : [])
                            if (relatedUids.length > 0) {
                                relatedData = relatedItemsForField.items.filter((item: any) => relatedUids.includes(item.uid))
                            } else {
                                // No values set, but include all available items for reference
                                relatedData = { availableItems: relatedItemsForField.items }
                            }
                        }
                    } else if (relatedItemsForField) {
                        // Field found but no items
                        relatedData = { message: 'No items found for this relation field', field: field.value }
                    } else {
                        // Field not found in relatedItems
                        relatedData = { message: 'Related items not loaded for this field', field: field.value, availableFields: relatedItems.map((r: any) => r.field) }
                    }
                }
            }

            return (
                <Card
                    key={field.name}
                    className={leadingInstruction
                        ? "mb-3 sm:mb-4 border-0 bg-transparent shadow-none"
                        : "mb-3 sm:mb-4 hover:shadow-md transition-shadow"}
                >
                    <CardContent className="p-3 sm:p-4">
                        <div className="space-y-2 sm:space-y-3">
                            {leadingInstruction && (
                                renderInstructionContent(leadingInstruction)
                            )}
                            {!hideHeader && (
                                <div className="flex items-center space-x-2">
                                    {getFieldIcon(effectiveFieldType)}
                                    <Label htmlFor={field.name} className="text-sm font-medium text-gray-900 flex-1 min-w-0">
                                        {field.label}
                                    </Label>
                                    <Badge variant="secondary" className="text-xs flex-shrink-0">
                                        {effectiveFieldType}
                                    </Badge>
                                    <DebugFieldButton
                                        fieldConfig={field}
                                        fieldValue={field.type === 'systemrelation' ? getSystemRelationValue(_currentItem, field) : _currentItem[field.name]}
                                        relatedData={relatedData}
                                        label={field.label}
                                    />
                                </div>
                            )}
                            {field.description && (
                                <p className="text-xs text-gray-500 mt-1">
                                    {field.description}
                                </p>
                            )}
                            {content}
                        </div>
                    </CardContent>
                </Card>
            )
        }

        switch (effectiveFieldType) {
            case "instruction": {
                if (nextField) {
                    return null;
                }
                const instructionName = field.name || field.key || `instruction_${field.uid || "field"}`;
                return (
                    <Card key={field.name || instructionName} className="mb-3 sm:mb-4 border-0 bg-transparent shadow-none">
                        <CardContent className="p-3 sm:p-4">
                            {field.description && (
                                <p className="text-xs text-gray-500 mb-2">
                                    {field.description}
                                </p>
                            )}
                            {renderInstructionContent(field)}
                        </CardContent>
                    </Card>
                )
            }
            case "text":
                return fieldCard(
                    <Input
                        id={field.name}
                        name={field.name}
                        defaultValue={currentItem[field.name] || ''}
                        onChange={(event) => handleTextInputChange(event, field)}
                        placeholder={`Enter ${field.label.toLowerCase()}`}
                        className="w-full"
                    />
                )
            case "textarea":
                return fieldCard(
                    <textarea
                        id={field.name}
                        name={field.name}
                        defaultValue={currentItem[field.name] || ''}
                        onChange={(event) => handleTextInputChange(event, field)}
                        placeholder={`Enter ${field.label.toLowerCase()}`}
                        className="w-full min-h-[120px] rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                        rows={4}
                    />
                )
            case "image":
                return fieldCard(
                    <div className="space-y-3">
                        <Image
                            filename={pictureFilename}
                            alt={meta.label}
                            onChange={(img: any) => handleImageChange(field.name, img)}
                            parent={_currentItem}
                            connectorAssetPublicUrl={connectorAssetPublicUrl}
                            allowConnectorUpload={Boolean(connectorAssetPublicUrl)}
                            collectionSlug={meta?.slug || slug || collectionFromRedux}
                            fieldName={field.name}
                        />
                    </div>
                )
            case "file": {
                const folderName = getMongoCollectionNameForFolder(meta, slug) || collectionFromRedux || slug;
                const fileFieldValue = (pendingFileValues[field.name] ?? _currentItem[field.name] ?? currentItem[field.name] ?? '') as string;
                return fieldCard(
                    <div className="space-y-3">
                        <FileField
                            filename={fileFieldValue}
                            label={field.label}
                            onChange={(data) => handleFileChange(field.name, data)}
                            parent={_currentItem}
                            collection={folderName}
                            fieldName={field.name}
                            temporaryUpload={isNew}
                        />
                    </div>
                )
            }
            case "relation":
                return fieldCard(
                    <RelationField
                        key={field.label}
                        table={field.value}
                        label={field.label}
                        allowedGroups={user?.info?.groups}
                        fields={field.fields}
                        relationFieldKey={field.name}
                        readOnlyUids={[]}
                        canEdit={true}
                        onChange={handleDropdownChange}
                        listOrganization={meta?.organization}
                    />
                )
            case "systemrelation": {
                const sourceValue = getSystemRelationValue(_currentItem, field);
                const readOnlyUids = sourceValue ? [sourceValue] : [];
                return fieldCard(
                    <RelationField
                        key={field.label}
                        table={field.value}
                        systemSource={field.value}
                        label={field.label}
                        allowedGroups={user?.info?.groups}
                        fields={field.fields}
                        relationFieldKey={field.name}
                        readOnlyUids={readOnlyUids}
                        canEdit={false}
                        onChange={() => { }}
                        listOrganization={meta?.organization}
                    />
                )
            }
            case "multiplerelation": {
                const isUsersComponent = (meta?.slug === 'users') || (slug === 'users') || (meta?.collection === 'users');
                const searchGroups = isUsersComponent && field.name === 'groups';
                return fieldCard(
                    <RelationField
                        key={field.label}
                        table={field.value}
                        label={field.label}
                        allowedGroups={user?.info?.groups}
                        fields={field.fields}
                        relationFieldKey={field.name}
                        readOnlyUids={[]}
                        multiple={true}
                        canEdit={true}
                        enableSearch={searchGroups}
                        onChange={handleDropdownChange}
                        listOrganization={meta?.organization}
                    />
                )
            }
            case "checkbox":
                return fieldCard(
                    <div className="flex items-center space-x-2">
                        <Checkbox
                            id={field.name}
                            checked={(_currentItem[field.name] ?? currentItem[field.name]) === true}
                            onCheckedChange={(checked) => handleCheckboxChange(checked as boolean, field.name)}
                        />
                        <Label htmlFor={field.name} className="text-sm text-gray-600">
                            {field.label}
                        </Label>
                    </div>,
                    { hideHeader: true }
                )
            case "richtext": {
                const fieldName = field.name || field.key;
                if (!fieldName) {
                    return fieldCard(
                        <Alert>
                            <AlertCircle className="h-4 w-4" />
                            <AlertDescription>
                                Rich text field is missing a name.
                            </AlertDescription>
                        </Alert>
                    );
                }
                const richTextValue = _currentItem[fieldName] ?? currentItem[fieldName];
                return fieldCard(
                    <div className="min-h-[200px]">
                        <RichTextEdit
                            key={`${currentItem?.uid || "new"}-${fieldName}-${formResetToken}`}
                            instanceKey={`${currentItem?.uid || "new"}-${fieldName}-${formResetToken}`}
                            name={fieldName}
                            value={richTextValue}
                            onChange={handleHtmlChange}
                        />
                    </div>
                )
            }
            case "calendar":
                return fieldCard(
                    <EditCalendar
                        onChange={handleCalendarChange}
                        name={field.name}
                        value={currentItem[field.name]}
                    />
                )
            case "datetime":
                return fieldCard(
                    <DateTime
                        value={currentItem[field.name]}
                        onChange={(datetime: any) => handleDateTimeChange({ datetime: datetime, name: field.name })}
                    />
                )
            case "dateonly":
                return fieldCard(
                    <DateOnly
                        value={currentItem[field.name]}
                        onChange={(date: any) => handleDateTimeChange({ datetime: date, name: field.name })}
                    />
                )
            case "timeonly":
                return fieldCard(
                    <TimeOnly
                        value={currentItem[field.name]}
                        onChange={(time: any) => handleDateTimeChange({ datetime: time, name: field.name })}
                    />
                )
            case "date":
                return fieldCard(
                    <DateOnly
                        value={currentItem[field.name]}
                        onChange={(date: any) => handleDateTimeChange({ datetime: date, name: field.name })}
                    />
                )
            case "number":
                return fieldCard(
                    <Input
                        id={field.name}
                        name={field.name}
                        type="number"
                        defaultValue={currentItem[field.name] !== undefined && currentItem[field.name] !== null ? currentItem[field.name] : ''}
                        onChange={handleNumberChange}
                        placeholder={`Enter ${field.label.toLowerCase()}`}
                        className="w-full"
                    />
                )
            case "select":
            case "dropdown": {
                const options = parseSelectOptions(field.options ?? field.value);
                const rawVal = _currentItem[field.name] ?? currentItem[field.name] ?? '';
                return fieldCard(
                    <ItemSelectField field={field} options={options} rawVal={rawVal} />
                )
            }
            case "code": {
                const codeValue = _currentItem[field.name] ?? currentItem[field.name] ?? '';
                const displayValue = codeValue !== null && codeValue !== undefined && String(codeValue).trim() !== ''
                    ? String(codeValue)
                    : '';
                const generating = generatingCodeField === field.name;
                return fieldCard(
                    <div className="space-y-2">
                        <div className="flex items-center gap-2">
                            <Input
                                id={field.name}
                                readOnly
                                value={displayValue}
                                placeholder="No code set"
                                className="w-full bg-gray-50 font-mono"
                            />
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                disabled={generating}
                                onClick={async () => {
                                    if (!window.confirm('Generate a new code? This will replace the current value.')) {
                                        return;
                                    }
                                    const slugToUse = slug || meta?.slug;
                                    if (!slugToUse) return;
                                    setGeneratingCodeField(field.name);
                                    try {
                                        const res = await dataAPI.generateCode(slugToUse, field.name);
                                        if (res.success && res.data?.code != null) {
                                            dispatch({ type: ActionTypes.EDIT_ITEM_UPDATED, payload: { key: field.name, value: res.data.code } });
                                        }
                                    } finally {
                                        setGeneratingCodeField(null);
                                    }
                                }}
                            >
                                {generating ? 'Generating…' : 'Generate new code'}
                            </Button>
                        </div>
                    </div>
                );
            }
            case "gpx": {
                const slugToUse = slug || meta?.slug;
                const itemId =
                    _currentItem?.uid ||
                    currentItem?.uid ||
                    _currentItem?._id ||
                    currentItem?._id;
                return fieldCard(
                    <GpxFieldControl
                        value={_currentItem[field.name] ?? currentItem[field.name]}
                        fieldName={field.name}
                        label={field.label}
                        itemId={itemId}
                        collectionSlug={slugToUse}
                        onChange={(next) => {
                            dispatch({
                                type: ActionTypes.EDIT_ITEM_UPDATED,
                                payload: { key: field.name, value: next },
                            });
                        }}
                        onStartRecording={async () => {
                            if (!slugToUse || !itemId) return;
                            await gpxSetRecordingAPI(slugToUse, String(itemId), field.name, "start");
                        }}
                        onStopRecording={async () => {
                            if (!slugToUse || !itemId) return;
                            await gpxSetRecordingAPI(slugToUse, String(itemId), field.name, "stop");
                        }}
                        onAppendPoint={async (point) => {
                            if (!slugToUse || !itemId) return;
                            await appendGpxTrackPointAPI(slugToUse, String(itemId), {
                                field: field.name,
                                point,
                            });
                        }}
                    />
                );
            }
            default:
                return fieldCard(
                    <Alert>
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription>
                            Unknown field type: {field.type} ({field.name})
                        </AlertDescription>
                    </Alert>
                )
        }
    }

    useEffect(() => {
        if (!slug || !_meta || !Array.isArray(_meta) || _meta.length === 0) {
            return;
        }

        const byKey = tableListCtxKey ? _meta.find((m: any) => m && m.tableKey === tableListCtxKey) : undefined;
        const matchedMeta =
            byKey && byKey.slug === slug ?
                byKey :
                _meta.find((m: any) => m && m.slug === slug);
        setMeta(matchedMeta ?? null);
    }, [_meta, slug, tableListCtxKey])

    // Fetch related items only when meta actually changes (using meta.uid as dependency)
    useEffect(() => {
        if (!meta?.uid) return;

        fetchRelatedItems(meta).then((data) => {
            setRelatedItems(data)
        })
    }, [meta?.uid])

    return (
        meta ? (
            <div className="flex flex-col h-full">
                {/* Header with Title and Buttons - shown when used standalone (not in dialog) */}
                {!hideFooterButton && (
                    <div className="flex-shrink-0 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50 px-4 sm:px-6 py-3 sm:py-4">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2 sm:space-x-3 min-w-0 flex-1">
                                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                    {isNew ? (
                                        <Plus className="w-4 h-4 sm:w-6 sm:h-6 text-blue-600" />
                                    ) : (
                                        <Save className="w-4 h-4 sm:w-6 sm:h-6 text-blue-600" />
                                    )}
                                </div>
                                <div className="min-w-0 flex-1">
                                    <h2 className="text-lg sm:text-xl font-semibold text-gray-900 truncate">
                                        {isNew ? `Add New ${meta.name || 'Item'}` : `Edit ${meta.name || 'Item'} item`}
                                    </h2>
                                </div>
                            </div>
                            <div className="flex items-center space-x-2">
                                {onCancel && (
                                    <Button
                                        variant="outline"
                                        onClick={onCancel}
                                        className="px-4 py-2"
                                    >
                                        Cancel
                                    </Button>
                                )}
                                <Button
                                    onClick={handleUpdateItem}
                                    disabled={!valid || isLoading}
                                    className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2"
                                >
                                    {isLoading ? (
                                        <div className="flex items-center space-x-2">
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                            <span className="hidden sm:inline">Saving...</span>
                                        </div>
                                    ) : (
                                        <div className="flex items-center space-x-2">
                                            {isNew ? <Plus className="w-4 h-4" /> : <Save className="w-4 h-4" />}
                                            <span>{isNew ? (meta.name ? `Add to ${meta.name}` : "Add") : "Update"}</span>
                                        </div>
                                    )}
                                </Button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Scrollable Fields Container */}
                <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 space-y-4 edit-dialog-scrollable">
                    {/* Form Fields */}
                    <div className="space-y-3 sm:space-y-4">
                        {meta?.fields?.map((field: any, index: number) => (
                            <Fragment key={field.uid ?? field.name ?? index}>
                                {renderField(field, currentItem)}
                            </Fragment>
                        )) ?? []}
                    </div>

                    {/* Validation Warning */}
                    {!valid && (
                        <Alert variant="destructive">
                            <AlertCircle className="h-4 w-4" />
                            <AlertDescription>
                                You do not have permission to save this item.
                            </AlertDescription>
                        </Alert>
                    )}

                    {followUpNotice && (
                        <Alert className="border-blue-200 bg-blue-50 text-blue-900">
                            <AlertCircle className="h-4 w-4" />
                            <AlertDescription>
                                {followUpNotice}
                            </AlertDescription>
                        </Alert>
                    )}

                    {errorMessage && (
                        <Alert variant="destructive" className="bg-red-50 border-red-200 text-red-900">
                            <AlertCircle className="h-4 w-4" />
                            <AlertDescription>
                                {errorMessage}
                            </AlertDescription>
                        </Alert>
                    )}

                </div>
            </div>
        ) : (
            <div className="p-6">
                <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                        This component cannot be edited. Please select another component, or contact your administrator if this seems wrong.
                    </AlertDescription>
                </Alert>
            </div>
        )
    )
})

EditItem.displayName = "EditItem"

export default EditItem